import React, { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Loader2, Users, Sparkles, GraduationCap, ShieldCheck, TrendingUp } from 'lucide-react';

const DAY_FULL = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };

const DAY_LABEL = { mon: 'Mo', tue: 'Tu', wed: 'We', thu: 'Th', fri: 'Fr', sat: 'Sa', sun: 'Su' };
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const resColor = (pct) => pct >= 70 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : pct >= 40 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-red-700 bg-red-50 border-red-200';

export const TeamsTab = ({ lookback, lineLabels }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modeling, setModeling] = useState(false);
  const [evalText, setEvalText] = useState(null);
  const [scenario, setScenario] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/caseloads/teams', { params: { lookback_days: lookback } })
      .then(({ data: d }) => { setData(d); if (d.last_eval) setEvalText(d.last_eval); })
      .catch(() => toast.error('Failed to load teams'))
      .finally(() => setLoading(false));
  }, [lookback]);

  const model = async () => {
    setModeling(true);
    try {
      const { data: d } = await api.post('/caseloads/teams/evaluate', { scenario, lookback_days: lookback });
      setEvalText(d);
      toast.success('Best teams modeled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Team modeling failed'); }
    setModeling(false);
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="caseloads-teams">
      <div className="rounded-xl border border-[#DED8EB] bg-white p-3 space-y-2" data-testid="teams-ai-box">
        <p className="text-xs text-[#8E85A0]">Draft pods built from real shift history: staff who already share participants, grouped with overlapping availability and cross-service-line reach. The AI refines them into the best training-consistent teams.</p>
        <textarea value={scenario} onChange={e => setScenario(e.target.value)} rows={1}
                  placeholder={'Optional team scenario — e.g. "Build 3 teams that each cover CPS + IHCS" · "Keep Maya and Jordan together"'}
                  className="w-full rounded-lg border border-[#DED8EB] px-3 py-2 text-sm text-[#2D1B4E] placeholder:text-[#B9B2C9] focus:outline-none focus:border-[#9B7ED8]"
                  data-testid="teams-scenario-input" />
        <Button onClick={model} disabled={modeling} className="bg-[#2D1B4E] hover:bg-[#3D2A63] text-white gap-1.5" data-testid="teams-model-btn">
          {modeling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {modeling ? 'Modeling…' : 'AI: Model best teams'}
        </Button>
      </div>

      {evalText && (
        <div className="rounded-xl border border-[#D9CBA8] bg-[#FDFBF4] p-4" data-testid="teams-eval">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#8A6D1F] flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> {evalText.scenario ? `Team scenario: "${evalText.scenario}"` : 'AI team plan'}
            </span>
            <span className="text-[10px] text-[#8E85A0]">{new Date(evalText.created_at).toLocaleString()} · {evalText.created_by}</span>
          </div>
          <div className="text-sm text-[#3A3045] whitespace-pre-wrap leading-relaxed" data-testid="teams-eval-text">
            {evalText.text.replace(/^### /gm, '• ').replace(/^## /gm, '— ').replace(/\*\*/g, '')}
          </div>
        </div>
      )}

      {data.teams.length === 0 && (
        <p className="text-sm text-[#8E85A0] py-8 text-center" data-testid="teams-empty">No shift history in this window to build teams from — try a longer lookback.</p>
      )}

      {data.teams.map(t => (
        <div key={t.id} className="rounded-xl border border-[#DED8EB] bg-white p-4" data-testid={`team-card-${t.id}`}>
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-extrabold text-[#2D1B4E] flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Users className="h-4 w-4 text-[#6B3FA0]" /> Team {t.id}
            </span>
            <span className="text-xs text-[#8E85A0]">{t.members.length} staff · {t.participants.length} participants</span>
            {t.service_lines.map(l => (
              <span key={l} className="text-[10px] font-bold uppercase text-[#6B3FA0] bg-[#F0EAFA] rounded-full px-2 py-0.5">{lineLabels?.[l] || l}</span>
            ))}
            <span className={`ml-auto text-[11px] font-bold border rounded-full px-2 py-0.5 flex items-center gap-1 ${resColor(t.resilience)}`} data-testid={`team-resilience-${t.id}`}>
              <ShieldCheck className="h-3 w-3" /> {t.resilience}% covered by 2+ staff
            </span>
          </div>

          <div className="mt-2.5 grid sm:grid-cols-2 gap-2">
            {t.members.map(m => (
              <div key={m.id} className="rounded-lg border border-[#E8E2F2] bg-[#FBFAFD] px-2.5 py-1.5 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-[#2D1B4E]">{m.name}</span>
                <span className="text-[10px] text-[#8E85A0]">{m.participant_count} ppl{m.avg_weekly_hours ? ` · ${m.avg_weekly_hours}h/wk` : ''}</span>
                <span className="ml-auto flex gap-0.5">
                  {DAY_KEYS.map(d => (
                    <span key={d} className={`text-[9px] font-black w-5 text-center rounded ${m.available_days.includes(d) ? 'bg-[#E8F5EE] text-[#2D6A4F]' : 'bg-gray-100 text-gray-300'}`}>{DAY_LABEL[d]}</span>
                  ))}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-2 flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-bold uppercase text-[#8E85A0] mr-1">Team day coverage:</span>
            {DAY_KEYS.map(d => (
              <span key={d} className={`text-[10px] font-black w-7 h-5 flex items-center justify-center rounded ${t.day_coverage.includes(d) ? 'bg-[#6B3FA0] text-white' : 'bg-gray-100 text-gray-300'}`}>{DAY_LABEL[d]}</span>
            ))}
          </div>

          <div className="mt-2 flex gap-1.5 flex-wrap">
            {t.participants.map(p => (
              <span key={p.name} title={p.known_by.map(k => `${k.name} ${k.count}×`).join(', ')}
                    className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${p.depth >= 2 ? 'text-[#2D6A4F] bg-[#E8F5EE] border-[#BFE3CF]' : 'text-amber-800 bg-amber-50 border-amber-200'}`}>
                {p.name} <b>{p.depth >= 2 ? `${p.depth}✓` : '1 only'}</b>
              </span>
            ))}
          </div>

          {(t.expansion || []).length > 0 && (
            <div className="mt-2.5 rounded-lg border border-[#BFE3CF] bg-[#F2FBF6] p-2.5" data-testid={`team-expansion-${t.id}`}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#2D6A4F] flex items-center gap-1 mb-1">
                <TrendingUp className="h-3.5 w-3.5" /> Open capacity — where this team can add slots
              </span>
              <div className="space-y-0.5">
                {t.expansion.map((e, i) => (
                  <p key={i} className="text-[11px] text-[#3A3045]">
                    <b className="text-[#2D6A4F]">{DAY_FULL[e.day]}</b>: room for <b>+{e.open_slots} slot{e.open_slots > 1 ? 's' : ''}</b>
                    <span className="text-[#8E85A0]"> ({e.available_staff.length} staff available, {e.current_participants} participant{e.current_participants !== 1 ? 's' : ''} currently that day{e.staff_with_capacity.length > 0 ? ` — capacity: ${e.staff_with_capacity.map(s => `${s.name}${s.avg_weekly_hours ? ` ${s.avg_weekly_hours}h/wk` : ''}`).join(', ')}` : ''})</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {t.cross_training.length > 0 && (
            <div className="mt-2.5 rounded-lg border border-[#C9DDF2] bg-[#F4F8FD] p-2.5" data-testid={`team-training-${t.id}`}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D4E89] flex items-center gap-1 mb-1">
                <GraduationCap className="h-3.5 w-3.5" /> Cross-training priorities
              </span>
              <div className="space-y-0.5">
                {t.cross_training.map((g, i) => (
                  <p key={i} className="text-[11px] text-[#3A3045]">
                    <b>{g.participant}</b> only known by <b>{g.only_known_by}</b> → train <b className="text-[#1D4E89]">{g.train}</b>
                    <span className="text-[#8E85A0]"> ({g.day_match}% day match)</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TeamsTab;
